// v2/hooks/useAdminQuotes.js — Yonetim: Teklifler (backend: myapp/v2_admin_teklif.py)
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiGet, apiPost, qs, OTURUM_BITTI } from '../utils/api';
import { dosyaIndir } from './useQuotes';

const yol = (no, ek = '') => `/v2/admin/quotes/${encodeURIComponent(no)}/${ek}`;

/** Tum teklifler listesi (filtre + sayfa + istatistik) */
export function useAdminQuoteList(params) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const sorgu = qs(params);

  const yenile = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    try {
      const veri = await apiGet(`/v2/admin/quotes/${sorgu}`, { signal: ctrl.signal });
      if (ctrl.signal.aborted) return;
      setData(veri);
    } catch (e) {
      if (e && (e.name === 'AbortError' || e.message === OTURUM_BITTI)) return;
      setError((e && e.message) || 'Teklifler alınamadı');
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, [sorgu]);

  useEffect(() => {
    yenile();
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [yenile]);

  return { data, loading, error, yenile };
}

/** Tek teklif (yonetim) */
export function useAdminQuote(no) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tik, setTik] = useState(0);
  const yenile = useCallback(() => setTik((t) => t + 1), []);

  useEffect(() => {
    let iptal = false;
    setLoading(true);
    apiGet(yol(no))
      .then((v) => { if (!iptal) { setQuote(v.quote); setError(null); setLoading(false); } })
      .catch((e) => {
        if (iptal || e.message === OTURUM_BITTI) return;
        setError(e.status === 404 ? 'Teklif bulunamadı' : (e.message || 'Teklif alınamadı'));
        setLoading(false);
      });
    return () => { iptal = true; };
  }, [no, tik]);

  return { quote, setQuote, loading, error, yenile };
}

/** Kalemler + canli stok */
export function useAdminQuoteLines(no, { status, q, page, per }) {
  const [state, setState] = useState({
    lines: [], totalItems: 0, totalPages: 1, loading: true, refreshing: false, error: null,
  });
  const [tik, setTik] = useState(0);
  const yenile = useCallback(() => setTik((t) => t + 1), []);
  const ilk = useRef(true);

  useEffect(() => {
    let iptal = false;
    setState((s) => ({ ...s, loading: ilk.current, refreshing: !ilk.current, error: null }));
    apiGet(`${yol(no, 'lines/')}${qs({ status: status === 'all' ? '' : status, q, page, per_page: per })}`)
      .then((v) => {
        if (iptal) return;
        ilk.current = false;
        setState({
          lines: v.results || [], totalItems: v.total_items || 0, totalPages: v.total_pages || 1,
          loading: false, refreshing: false, error: null,
        });
      })
      .catch((e) => {
        if (iptal || e.message === OTURUM_BITTI) return;
        setState((s) => ({ ...s, loading: false, refreshing: false, error: e.message || 'Kalemler alınamadı' }));
      });
    return () => { iptal = true; };
  }, [no, status, q, page, per, tik]);

  const kalemGuncelle = useCallback((yeni) => {
    setState((s) => ({
      ...s,
      lines: s.lines.map((l) => (l.id === yeni.id ? { ...yeni, stock: l.code === yeni.code ? l.stock : null } : l)),
    }));
  }, []);

  return { ...state, yenile, kalemGuncelle };
}

export const adminQuoteApi = {
  customers(q, signal) {
    return apiGet(`/v2/admin/quotes/customers/${qs({ q })}`, { signal });
  },
  create(cari, file, note) {
    const fd = new FormData();
    fd.append('cari', cari);
    fd.append('file', file);
    if (note) fd.append('note', note);
    return apiPost('/v2/admin/quotes/create/', fd);
  },
  share(no, shared) {
    return apiPost(yol(no, 'share/'), { shared });
  },
  resolve(no, lineId, code) {
    return apiPost(yol(no, `lines/${lineId}/resolve/`), { code });
  },
  renew(no) {
    return apiPost(yol(no, 'renew/'), {});
  },
  remove(nos) {
    return apiPost('/v2/admin/quotes/delete/', { nos });
  },
  download(no, tur, scope) {
    const uz = tur === 'excel' ? 'xlsx' : 'pdf';
    const ek = scope === 'supplied' ? '?scope=supplied' : '';
    return dosyaIndir(`${yol(no, `${tur}/`)}${ek}`, `${no}.${uz}`);
  },
};
